import React from "react";
import {
  FilterInfo,
  LessonOrUnitGroup,
  LessonOrUnitGroupKind,
} from "../common/api";
import { UnitItem, UnitItemLoggingContext } from "../components/UnitItem";
import { UnitFilterControls, useUnitFilters } from "../components/UnitFilters";
import { UnitSearchResults, useUnitSearch } from "../components/UnitSearch";
import { ApiContext } from "../common/context/ApiContext";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { AccountContext } from "../common/context/AccountContext";
import { useLiveData } from "../common/utils";
import { default as UUID } from "uuid";
import {
  AnalyticsContext,
  ViewPracticeEvent,
} from "../common/context/AnalyticsContext";
import { useTranslation } from "react-i18next";
import {
  EmptyResults,
  Section,
  SectionHeading,
  UnitTilesRow,
} from "../common/CommonStyles";

type PracticeState = {
  groups: [LessonOrUnitGroup] | null;
  filterInfo?: FilterInfo;
};

export const PracticePage: React.FC = () => {
  const [state, setState] = React.useState<PracticeState>({ groups: null });

  const { setCurrentTab } = React.useContext(TabsContext);

  const { getPracticeMetadata } = React.useContext(ApiContext);

  const { startLoading, stopLoading } = useLoadingIndicator(true);

  const { accountLiveData } = React.useContext(AccountContext);
  const discoverFetchedForProfileStateVersion = React.useRef<number | null>(
    null
  );
  const fetchJobForProfileStateVersion = React.useRef<number | null>(null);
  const currentFetchJobUuid = React.useRef<string | null>(null);
  const timeoutId = React.useRef<number>();
  const { logEvent } = React.useContext(AnalyticsContext);
  const { t } = useTranslation();
  const filters = useUnitFilters(TabEnum.Practice, state.filterInfo);
  const search = useUnitSearch(state.groups !== null);

  React.useEffect(() => {
    logEvent(new ViewPracticeEvent());
  }, [logEvent]);

  React.useEffect(() => {
    setCurrentTab(TabEnum.Practice);
    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId.current);
      }
    };
  }, [setCurrentTab]);

  const maybeRefetchPractice = React.useCallback(async () => {
    const account = accountLiveData.value;
    if (account.hasMaybeProfileState()) {
      if (
        account.profileStateCurrentVersion !==
          discoverFetchedForProfileStateVersion.current &&
        account.profileStateCurrentVersion !==
          fetchJobForProfileStateVersion.current
      ) {
        startLoading();
        const fetchJobUuid = (currentFetchJobUuid.current = UUID.v4());
        fetchJobForProfileStateVersion.current =
          account.profileStateCurrentVersion;
        const practiceMetadata = await getPracticeMetadata(
          account.profile?.id || null,
          account.profileState?.lessons_in_progress || {}
        );
        if (currentFetchJobUuid.current !== fetchJobUuid) {
          return;
        }
        setState({
          groups: practiceMetadata.groups,
          filterInfo: practiceMetadata.filter_info,
        });
        discoverFetchedForProfileStateVersion.current =
          fetchJobForProfileStateVersion.current;
        stopLoading();
        timeoutId.current = window.setTimeout(() => {
          currentFetchJobUuid.current = UUID.v4();
          discoverFetchedForProfileStateVersion.current = null;
          fetchJobForProfileStateVersion.current = null;
          maybeRefetchPractice();
        }, 8 * 60 * 60 * 1000);
      }
    }
  }, [accountLiveData, getPracticeMetadata, startLoading, stopLoading]);

  useLiveData(maybeRefetchPractice, accountLiveData);

  if (!state.groups) {
    return <></>;
  }

  const loggingContextFor = (group: LessonOrUnitGroup) =>
    group.group === LessonOrUnitGroupKind.GameUnits
      ? UnitItemLoggingContext.Game
      : UnitItemLoggingContext.Flashpack;

  if (search.committedQuery !== null) {
    return (
      <>
        <UnitFilterControls filters={filters} search={search} />
        <UnitSearchResults search={search} />
      </>
    );
  }

  const filteredGroups = state.groups
    .map((group) => ({
      group,
      units: (group.units ?? []).filter(filters.matches),
    }))
    .filter(({ units }) => units.length > 0);

  return (
    <>
      <UnitFilterControls filters={filters} search={search} />
      {filteredGroups.length === 0 && filters.isFiltering ? (
        <EmptyResults message={t("filter_no_results")} />
      ) : (
        filteredGroups.map(({ group, units }) => (
          <Section key={group.group}>
            <SectionHeading>{group.title}</SectionHeading>
            <UnitTilesRow>
              {units.map((unit) => (
                <UnitItem
                  key={unit.id}
                  unit={unit}
                  loggingContext={loggingContextFor(group)}
                />
              ))}
            </UnitTilesRow>
          </Section>
        ))
      )}
    </>
  );
};
